import { useState } from 'react';
import intl from 'react-intl-universal';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { parseISO } from 'date-fns';

import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  Theme,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  GridOn as IconOpenTile,
  List as OpenQuestionList,
  PlaylistAddCheck as IconDecisionLog,
  LocalShippingOutlined as OpenDeliveriesList,
  DateRange as WeekIcon,
} from '@mui/icons-material';

import {
  LIST_BOARD_VIEW_MODE,
  LIST_DELIVERY_VIEW_MODE,
  LIST_MILESTONE_VIEW_MODE,
  WEEK_BOARD_VIEW_MODE,
} from '../../utils/ViewModeUtil';
import OpenMilestonesList from '../../common/icons/OutlinedMilestoneIcon';

import { Board } from '@/models/BoardModel';

const useStyles = (theme: Theme) => ({
  card: {
    maxWidth: 400,
    backgroundColor: '#fafafa',
  },
  header: {
    paddingTop: '8px',
    paddingBottom: '0px',
  },
  actions: {
    display: 'flex',
  },
  expand: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    marginLeft: 'auto',
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
});

const BoardItem = ({ board }: { board: Board }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [expanded, setExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  const communityId = searchParams.get('communityId') || '';
  const roomId = searchParams.get('roomId') || '';
  const navigate = useNavigate();

  const handleOpenTileView = () => navigate(`/${communityId}/${roomId}/boards/${board.id}`);
  const handleOpenWeekView = () =>
    navigate(`/${communityId}/${roomId}/boards/${board.id}?view=${WEEK_BOARD_VIEW_MODE}`);
  const handleOpenListView = () =>
    navigate(`/${communityId}/${roomId}/boards/${board.id}?view=${LIST_BOARD_VIEW_MODE}`);
  const handleOpenDecisionLogView = () =>
    navigate(
      `/${communityId}/${roomId}/boards/${board.id}?view=${LIST_BOARD_VIEW_MODE}&step=${board.step_config.decision_log_step_id}`,
    );
  const handleOpenMilestoneListView = () =>
    navigate(`/${communityId}/${roomId}/boards/${board.id}?view=${LIST_MILESTONE_VIEW_MODE}`);
  const handleOpenDeliveryListView = () =>
    navigate(`/${communityId}/${roomId}/boards/${board.id}?view=${LIST_DELIVERY_VIEW_MODE}`);
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card sx={classes.card}>
      <CardHeader sx={classes.header} title={board.name} />
      <CardActions sx={classes.actions} disableSpacing>
        <Tooltip title={intl.get('boards.tooltips.tile_view')}>
          <IconButton onClick={handleOpenTileView} className={'Open-tile-view'}>
            <IconOpenTile />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.get('boards.tooltips.week_view')}>
          <IconButton onClick={handleOpenWeekView} className={'Open-week-view'}>
            <WeekIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.get('boards.tooltips.decision_log')}>
          <IconButton onClick={handleOpenDecisionLogView} className={'Open-decision-log-view'}>
            <IconDecisionLog />
          </IconButton>
        </Tooltip>
        {board.milestones_config.length ? (
          <Tooltip title={intl.get('boards.tooltips.milestone_list_view')}>
            <IconButton onClick={handleOpenMilestoneListView} className={'Open-milestone-list-view'}>
              <OpenMilestonesList />
            </IconButton>
          </Tooltip>
        ) : (
          <span />
        )}
        <Tooltip title={intl.get('boards.tooltips.question_list_view')}>
          <IconButton onClick={handleOpenListView} className={'Open-question-list-view'}>
            <OpenQuestionList />
          </IconButton>
        </Tooltip>
        {board.delivery_config ? (
          <Tooltip title={intl.get('boards.tooltips.delivery_list_view')}>
            <IconButton onClick={handleOpenDeliveryListView} className={'Open-delivery-list-view'}>
              <OpenDeliveriesList />
            </IconButton>
          </Tooltip>
        ) : (
          <span />
        )}
        <Tooltip title={intl.get('boards.tooltips.expand')}>
          <IconButton
            sx={{ ...classes.expand, [classes.expandOpen]: expanded }}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label={intl.get('boards.tooltips.expand')}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {board.creation_date && (
            <Typography>{intl.get('boards.creation_date', { date: parseISO(board.creation_date) })}</Typography>
          )}
          {board.last_modified_date && (
            <Typography>
              {intl.get('boards.last_modified_date', { date: parseISO(board.last_modified_date) })}
            </Typography>
          )}
          <Typography component="p">{intl.get('boards.description', { description: board.description })}</Typography>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default BoardItem;
